import { deg2rad } from "../math/angles";
import { simpson } from "../math/integrate";
import { area, centroid, clipAbove, type Pt } from "../math/polygon";
import type { Section } from "../geometry/section";
import { rcFrame, rcStemBackXAt, rcStemFrontXAt, rcStemThicknessAt } from "../geometry/rcCantilever";
import { buildSegments, integrateSegments } from "../pressure/diagram";
import { lineLoadPressure, stripLoadPressure } from "../pressure/surcharge";
import { hydrodynamic, hydrostatic } from "../pressure/water";
import { passiveCoefficient } from "../pressure/coefficients";
import type { CaseParams, CaseLoads } from "../loads/assemble";
import type { CaseId, Force, ForceKind } from "../types/result";
import type { RcCantileverGeometry, WallInput } from "../types/input";
import type { RcCombination } from "./codes";
import type { WarningCode } from "../warnings";

/** Yük türü → kombinasyon çarpanı anahtarı */
export type LoadClass = "G" | "Q" | "H" | "F" | "E";
export const LOAD_CLASS: Record<ForceKind, LoadClass | null> = {
  weight: "G", wedge: "G", surchargeOnWedge: "Q", earthStatic: "H", lineLoad: "H", water: "F", uplift: "F", frontWater: "F",
  earthDynamic: "E", inertia: "E", waterDynamic: "E", passive: null,
};
export type ClassSums = Record<LoadClass, number>;
const zeroSums = (): ClassSums => ({ G: 0, Q: 0, H: 0, F: 0, E: 0 });
export const factored = (s: ClassSums, c: RcCombination): number => s.G * c.G + s.Q * c.Q + s.H * c.H + s.F * c.F + s.E * c.E;

/** Kombinasyon × yük durumu: faktörlü taban basıncı dağılımı */
export interface DesignCase {
  id: string; label: string; caseId: CaseId; combo: RcCombination;
  Nd: number; MdToe: number; xbar: number; eD: number; qToe: number; qHeel: number; contactLength: number;
  /** Burundan x'te taban basıncı (kN/m²), çekmesiz */
  qAt: (x: number) => number;
}

export function designCaseFor(loads: CaseLoads, caseId: CaseId, combo: RcCombination, B: number): DesignCase {
  const V = zeroSums(), M = zeroSums();
  for (const f of loads.forces) {
    const k = LOAD_CLASS[f.kind];
    if (!k) continue;
    V[k] += f.V;                         // kaldırma negatif
    M[k] += f.Mres - f.Movr;             // burun etrafında net (direnç yönü +)
  }
  const Nd = factored(V, combo), MdToe = factored(M, combo);
  const xbar = Nd > 1e-9 ? MdToe / Nd : NaN;
  const eD = B / 2 - xbar;
  let qToe: number, qHeel: number, contactLength: number, qAt: (x: number) => number;
  if (!(Nd > 1e-9) || !Number.isFinite(xbar) || xbar <= 0 || xbar >= B) {
    qToe = qHeel = Infinity; contactLength = 0; qAt = () => Infinity;
  } else if (Math.abs(eD) <= B / 6 + 1e-9) {
    qToe = (Nd / B) * (1 + (6 * eD) / B); qHeel = (Nd / B) * (1 - (6 * eD) / B); contactLength = B;
    qAt = (x) => qToe + ((qHeel - qToe) * x) / B;
  } else if (eD > 0) {
    contactLength = 3 * xbar; qToe = (2 * Nd) / contactLength; qHeel = 0;
    qAt = (x) => (x < contactLength ? qToe * (1 - x / contactLength) : 0);
  } else {
    contactLength = 3 * (B - xbar); qHeel = (2 * Nd) / contactLength; qToe = 0;
    qAt = (x) => (x > B - contactLength ? qHeel * (1 - (B - x) / contactLength) : 0);
  }
  return { id: `${combo.id}/${caseId}`, label: combo.label, caseId, combo, Nd, MdToe, xbar, eD, qToe, qHeel, contactLength, qAt };
}

/** Kesit tesirleri türlere ayrılmış (karakteristik): kombinasyonla çarpılır */
export interface ClassEffects { M: ClassSums; V: ClassSums }
const zeroEffects = (): ClassEffects => ({ M: zeroSums(), V: zeroSums() });

export interface StemContext {
  input: WallInput; g: RcCantileverGeometry; section: Section; cp: CaseParams;
  warn: (code: WarningCode) => void;
}

/**
 * Gövde: `level` kotundaki kesitte (taban üstü kotu tf'den yukarı), üstteki yatay yüklerden moment ve kesme (birim uzunluk).
 * Toprak itkisi gövde arkasındaki düşey düzlemde (ψ = 90°), derinlik dolgu yüzeyinden; yalnız yatay bileşen (F·cosδ).
 */
export function stemEffects(ctx: StemContext, level: number): ClassEffects {
  const { input, g, section, cp } = ctx;
  const e = zeroEffects();
  const isSeismic = cp.id !== "static";
  const kh = isSeismic ? cp.kh : 0, kvF = isSeismic ? cp.kvFactor : 1;
  const f = rcFrame(g);
  const yC = section.yTop;                                   // gövde tepesi arka köşesinde dolgu yüzeyi = tepe kotu
  const H = yC - level;
  if (H <= 1e-9) return e;
  const warnAny = ctx.warn;
  const plane = { psi: Math.PI / 2, yC, yBot: level };
  const segsQ = buildSegments({ section, backfill: input.backfill, water: input.water, surcharge: input.loads.surcharge, kh, kvFactor: kvF, kvOnTotal: input.seismic.kvOnTotalPressure === true, warn: warnAny, plane });
  const segs0 = input.loads.surcharge > 0 ? buildSegments({ section, backfill: input.backfill, water: input.water, surcharge: 0, kh, kvFactor: kvF, kvOnTotal: input.seismic.kvOnTotalPressure === true, warn: warnAny, plane }) : segsQ;
  const fQ = integrateSegments(segsQ, input.loads.surcharge, kvF), f0 = integrateSegments(segs0, 0, kvF);
  const yDynFrac = input.seismic.dynamicPointOfApplication === "0.6H" ? 0.6 : 0.5;
  for (let i = 0; i < segsQ.length; i++) {
    const sq = fQ[i]!, s0 = f0[i]!, cosD = Math.cos(sq.delta);
    // Statik: toprak (H) = sürşarjsız; sürşarj payı (Q) = fark (bileşke kotları ayrı)
    const H0 = s0.Fstatic * cosD, HQ = sq.Fstatic * cosD - H0;
    const y0 = yC - s0.zStatic, yq = HQ > 1e-9 ? yC - (sq.Fstatic * sq.zStatic - s0.Fstatic * s0.zStatic) / (sq.Fstatic - s0.Fstatic) : y0;
    e.V.H += H0; e.M.H += H0 * (y0 - level);
    e.V.Q += HQ; e.M.Q += HQ * (yq - level);
    // Dinamik artım (E): tümü (sürşarj dahil) yükseklik oranında
    const Hd = sq.Fdyn * cosD;
    e.V.E += Hd; e.M.E += Hd * yDynFrac * H;
  }
  // Şerit / çizgisel yükler (H): basınç tam gövde yüksekliğiyle, kesit üstü derinliğe kadar integre; mesafe gövde arkasından (topuk boyu eklenir)
  const Hs = yC - f.tf;
  for (const L of input.loads.lineLoads) {
    const x = L.distance + f.heel;
    const p = (z: number): number => (L.kind === "strip" ? stripLoadPressure(L.magnitude, x, L.width ?? 0, z) : lineLoadPressure(L.magnitude, x, Hs, z));
    const F = simpson(p, 0, H), Mz = simpson((z) => p(z) * (H - z), 0, H);
    e.V.H += F; e.M.H += Mz;
  }
  // Su (F) ve hidrodinamik (E)
  const hw = input.water.backLevel !== null ? Math.max(0, input.water.backLevel - level) : 0;
  if (hw > 1e-9) {
    const hs = hydrostatic(hw, input.water.gammaWater);
    e.V.F += hs.F; e.M.F += hs.F * hs.y;
    if (isSeismic && input.backfill.permeability === "permeable") {
      const hd = hydrodynamic(hw, input.water.gammaWater, cp.khWater);
      e.V.E += hd.F; e.M.E += hd.F * hd.y;
    }
  }
  // Gövde ataleti (E): kesit üstündeki gövde parçası
  if (isSeismic && input.seismic.includeWallInertia && kh > 0) {
    const stem: Pt[] = [{ x: f.xStemFront, y: f.tf }, { x: f.xStemBack, y: f.tf }, { x: f.xTopBack, y: f.yTop }, { x: f.xTopFront, y: f.yTop }];
    const upper = clipAbove(stem, level);
    if (upper.length >= 3) {
      const W = input.material.gamma * area(upper), c = centroid(upper);
      e.V.E += kh * W; e.M.E += kh * W * (c.y - level);
    }
  }
  return e;
}

/** Topuk üstü dolgu sütununun x'teki ağırlığı (kN/m²): tabakalar ve su bantları; kvFactor·gFactor dışarıda uygulanır */
export function soilColumnWeight(input: WallInput, section: Section, x: number, yBase: number): number {
  const ySurf = section.surfaceYAt(x);
  if (ySurf <= yBase + 1e-9) return 0;
  const hw = input.water.backLevel;
  const gw = input.water.gammaWater;
  const nL = input.backfill.layers.length;
  const cuts = new Set<number>([yBase, ySurf]);
  let y = section.yTop;
  for (let j = 0; j < nL - 1; j++) { y -= input.backfill.layers[j]!.thickness; if (y > yBase + 1e-9 && y < ySurf - 1e-9) cuts.add(y); }
  if (hw !== null && hw > yBase + 1e-9 && hw < ySurf - 1e-9) cuts.add(hw);
  const ys = [...cuts].sort((a, b) => a - b);
  let w = 0;
  for (let i = 0; i + 1 < ys.length; i++) {
    const yMid = (ys[i]! + ys[i + 1]!) / 2;
    let li = nL - 1, yb = section.yTop;
    for (let j = 0; j < nL; j++) { yb -= input.backfill.layers[j]!.thickness; if (yMid > yb - 1e-9) { li = j; break; } }
    const L = input.backfill.layers[li]!;
    const sub = hw !== null && yMid < hw;
    const gamma = sub ? (L.gammaSat ?? L.gamma) : L.gamma;
    // Su altında: doygun ağırlık; kaldırma taban basıncında ayrıca (u) — burada toplam ağırlık
    w += gamma * (ys[i + 1]! - ys[i]!);
    void gw;
  }
  return w;
}

/** Taban altı su basıncı (kN/m²) burundan x'te (doğrusal) */
export function upliftAt(input: WallInput, B: number, x: number): number {
  const gw = input.water.gammaWater;
  const uHeel = gw * Math.max(0, input.water.backLevel ?? 0), uToe = gw * Math.max(0, input.water.frontLevel ?? 0);
  return uToe + ((uHeel - uToe) * x) / B;
}

export interface SlabContext { input: WallInput; g: RcCantileverGeometry; section: Section; cp: CaseParams; dc: DesignCase }

/**
 * Burun: yukarı taban basıncı (faktörlü, dc) − burun öz ağırlığı (G) [− kaldırma su (F) yukarı]. Kesit gövde ön yüzünde.
 * Döner: kesitteki tasarım M (kNm/m, + alt yüz çekme) ve V (kN/m). Basınç zaten faktörlü olduğundan yalnız öz ağırlık çarpanı uygulanır.
 */
export function toeDesignEffects(ctx: SlabContext): { Md: number; Vd: number } {
  const { input, g, cp, dc } = ctx;
  const f = rcFrame(g);
  const Lt = f.toe;
  if (Lt <= 1e-9) return { Md: 0, Vd: 0 };
  const isSeismic = cp.id !== "static";
  const kvF = isSeismic ? cp.kvFactor : 1, gF = isSeismic ? input.loads.selfWeightFactorSeismic : 1;
  const wSlab = input.material.gamma * f.tf * kvF * gF * dc.combo.G;
  const net = (x: number): number => dc.qAt(x) + upliftAt(input, f.B, x) * dc.combo.F - wSlab;   // yukarı +
  const Vd = simpson(net, 0, Lt), Md = simpson((x) => net(x) * (Lt - x), 0, Lt);
  return { Md, Vd };
}

/**
 * Topuk: aşağı yükler (plak G, dolgu sütunu G, sürşarj Q) − yukarı taban basıncı (faktörlü) [− kaldırma su F]. Kesit gövde arka yüzünde.
 * + üst yüz çekme. `heelUplift = false` ise taban basıncı ihmal (tutucu).
 */
export function heelDesignEffects(ctx: SlabContext): { Md: number; Vd: number; parts: { slab: number; soil: number; surcharge: number; pressure: number } } {
  const { input, g, section, cp, dc } = ctx;
  const f = rcFrame(g);
  const Lh = f.heel;
  if (Lh <= 1e-9) return { Md: 0, Vd: 0, parts: { slab: 0, soil: 0, surcharge: 0, pressure: 0 } };
  const isSeismic = cp.id !== "static";
  const kvF = isSeismic ? cp.kvFactor : 1, gF = isSeismic ? input.loads.selfWeightFactorSeismic : 1;
  const x0 = f.xStemBack;
  const wSlab = input.material.gamma * f.tf * kvF * gF * dc.combo.G;
  const soil = (x: number): number => soilColumnWeight(input, section, x, f.tf) * kvF * gF * dc.combo.G;
  const q = input.loads.surcharge * kvF * dc.combo.Q;
  const up = (x: number): number => (input.rc.heelUplift ? dc.qAt(x) : 0) + upliftAt(input, f.B, x) * dc.combo.F;
  const net = (x: number): number => wSlab + soil(x) + q - up(x);   // aşağı +
  const arm = (x: number): number => x - x0;
  const Vd = simpson(net, x0, f.B), Md = simpson((x) => net(x) * arm(x), x0, f.B);
  const parts = {
    slab: simpson((x) => wSlab * arm(x), x0, f.B), soil: simpson((x) => soil(x) * arm(x), x0, f.B),
    surcharge: simpson((x) => q * arm(x), x0, f.B), pressure: -simpson((x) => up(x) * arm(x), x0, f.B),
  };
  return { Md, Vd, parts };
}

/** Kayma dişi (basitleştirilmiş): diş yüzündeki pasif basınç artımı → kesme ve taban seviyesinde moment (H sınıfı) */
export function keyEffects(input: WallInput, g: RcCantileverGeometry, cp: CaseParams): { V: number; M: number; Kp: number } {
  if (!g.key) return { V: 0, M: 0, Kp: 0 };
  const fs = input.foundationSoil;
  const Kp = passiveCoefficient({ phi: deg2rad(fs.phi), beta: 0, psi: Math.PI / 2, theta: 0 });
  const Df = g.embedmentDepth, dk = g.key.depth;
  const p = (z: number): number => Kp * fs.gamma * (Df + z) + 2 * fs.c * Math.sqrt(Kp);
  const V = simpson(p, 0, dk), M = simpson((z) => p(z) * z, 0, dk);
  void cp;
  return { V, M, Kp };
}

export const stemThicknessAt = rcStemThicknessAt;
export const stemBackXAt = rcStemBackXAt;
export const stemFrontXAt = rcStemFrontXAt;
export type { Force };
